<script setup>
import notification from "@/utils/plugins/NotificationService";
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import TimeEntryTagService from "@/api/services/timeEntryTag.service";
import DepartmentService from "@/api/services/department.service";
import EmployeeService from "@/api/services/employee.service";
import TrackerProjectInfo from "@/components/shared/TrackerProjectInfo.vue";
import TrackerProjectSearch from "@/components/shared/TrackerProjectSearch.vue";
import TrackerFullReport from "@/components/shared/TrackerFullReport.vue";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";
import TrackerService from "@/api/services/tracker.service";
import { filterByNormalizedText } from "@/utils/textSearch";
import { prepareTrackerReport } from "@/utils/trackerReportTotals";


const authStore = useAuthStore();
const uiStore = useUiStore();
const user = computed(() => authStore.getUserLogged);

const recharge = ref(false);

const page = ref(1);
const itemsPerPage = ref(9999);
const optionsEmployees = ref([]);
const employeeSearchQuery = ref("");
const optionsClients = ref([]);
const optionsDepartments = ref([]);
const optionTags = ref([]);
const optionsClientsLoading = ref(false);
const description = ref("");
const employee = ref(null);
const client = ref(null);
const project = ref(null);
const department = ref(null);
const tags = ref([]);
const refReportTagsSelect = ref(null);
// eslint-disable-next-line no-unused-vars
const projects = ref([]);
const projectsText = computed(() => {
  if (projects.value.length < 1) {
    return "";
  } else {
    let text = "";
    for (let i = 0; i < projects.value.length; i++) {
      const element = projects.value[i];
      if (text === "") {
        text = element.name;
      } else {
        text = text + ", " + element.name;
      }
    }
    return text;
  }
});
const canGroupEntriesByDay = computed(() => {
  return (
    !!employee.value ||
    !!client.value ||
    !!project.value ||
    projects.value.length > 0 ||
    !!department.value ||
    tags.value.length > 0 ||
    !!description.value?.trim() ||
    (Array.isArray(dateRange.value) && dateRange.value.length > 0)
  );
});
const canDownloadReport = computed(() =>
  Array.isArray(dateRange.value) && dateRange.value.length === 2 && !!dateRange.value[0] && !!dateRange.value[1]
);
const filteredEmployeeOptions = computed(() => {
  return filterByNormalizedText(optionsEmployees.value, employeeSearchQuery.value, employee => employee.fullName);
});
// const dateStart = ref(null);
// const dateEnd = ref(null);
const dateRange = ref([]);
const dateShortcuts = [
  {
    text: 'Hoy',
    value: () => {
      const today = new Date();
      return [today, today];
    },
  },
  {
    text: 'Esta semana',
    value: () => {
      const now = new Date();
      const dayOfWeek = now.getDay() === 0 ? 7 : now.getDay();
      const monday = new Date(now);
      monday.setDate(now.getDate() - dayOfWeek + 1);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return [monday, sunday];
    },
  },
  {
    text: 'Semana pasada',
    value: () => {
      const now = new Date();
      const dayOfWeek = now.getDay() === 0 ? 7 : now.getDay();
      const monday = new Date(now);
      monday.setDate(now.getDate() - dayOfWeek - 6);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return [monday, sunday];
    },
  },
  {
    text: 'Este mes',
    value: () => {
      const now = new Date();
      return [
        new Date(now.getFullYear(), now.getMonth(), 1),
        new Date(now.getFullYear(), now.getMonth() + 1, 0),
      ];
    },
  },
  {
    text: 'Mes pasado',
    value: () => {
      const now = new Date();
      return [
        new Date(now.getFullYear(), now.getMonth() - 1, 1),
        new Date(now.getFullYear(), now.getMonth(), 0),
      ];
    },
  },
];

const pageEntries = ref(1);
const itemsPerPageEntries = ref(50);
const totalEntries = ref(0);
const totalTimeAll = ref(0);
const isActiveTotalHoursAll = ref(false);
const groupEntriesByDay = ref(false);

const dropdownProject = ref(false);
const refDropdownProject = ref();

const entries = ref([]);

watch(client, (newClient, oldClient) => {
  if (newClient?.id === oldClient?.id) {
    return;
  }

  project.value = null;
  projects.value = [];

  if (oldClient !== undefined) {
    search();
  }
});

watch(canGroupEntriesByDay, (isEnabled) => {
  if (!isEnabled) {
    groupEntriesByDay.value = false;
  }
});

watch(tags, () => {
  setSelectedTagTitles();
}, { deep: true });

onMounted(() => {
  getEmployees();
  getClients();
  getDepartments();
  getTags();
  search();
});

// Getters API
const getEmployees = () => {
  let params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value,
  };
  params["order[name]"] = "ASC";
  EmployeeService.getEmployees(params).then((res) => {
    optionsEmployees.value = res.data["hydra:member"];
  });
};
const filterEmployeesByName = (query) => {
  employeeSearchQuery.value = query;
};

const getClients = (query = "") => {
  const params = {
    search: query,
    limit: 500,
  };
  optionsClientsLoading.value = true;
  TrackerService.getClientSelector(params)
    .then((response) => {
      optionsClients.value = response.data["hydra:member"];
    })
    .catch((error) => {
      console.log(error);
      notification.addNotification({
        message: "Error al cargar los clientes",
        type: "error",
        duration: 6000,
      });
    })
    .finally(() => {
      optionsClientsLoading.value = false;
    });
};

const getDepartments = () => {
  let params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value,
  };
  params["order[name]"] = "ASC";
  uiStore.startLoading();
  DepartmentService.getDepartments(params)
    .then((response) => {
      optionsDepartments.value = response.data["hydra:member"];
    })
    .catch((error) => {
      console.log(error);
      notification.addNotification({
        message: "Error al cargar los departamentos",
        type: "error",
        duration: 6000,
      });
      uiStore.stopLoading();
    })
    .finally(() => {
      uiStore.stopLoading();
    });
};

const getTags = () => {
  let params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value,
  };
  params["order[name]"] = "ASC";
  uiStore.startLoading();
  TimeEntryTagService.getTags(params)
    .then((response) => {
      optionTags.value = response.data["hydra:member"];
    })
    .catch((error) => {
      console.log(error);
      notification.addNotification({
        message: "Error al cargar las etiquetas",
        type: "error",
        duration: 6000,
      });
      uiStore.stopLoading();
    })
    .finally(() => {
      uiStore.stopLoading();
    });
};

// Funcion Busqueda

const search = () => {
  recharge.value = true;
  // store.loading = true;
  uiStore.startLoading();
  const params = {
    itemsPerPage: itemsPerPageEntries.value,
    page: pageEntries.value,
    "order[timeStart]": "DESC",
  };
  isActiveTotalHoursAll.value = false;

  if (employee.value) {
    params["employee.id"] = employee.value.id;
    isActiveTotalHoursAll.value = true;
  }
  if (client.value) {
    params["project.client.id"] = client.value.id;
    isActiveTotalHoursAll.value = true;
  }
  if (projects.value && projects.value.length > 1) {
    const ids = projects.value.map((project) => project.id);
    params["project.id[]"] = ids;
    isActiveTotalHoursAll.value = true;
  }
  if (project.value && projects.value.length === 1) {
    params["project.id"] = project.value.id;
    isActiveTotalHoursAll.value = true;
  }
  if (description.value && description.value !== "") {
    params.description = description.value;
    isActiveTotalHoursAll.value = true;
  }
  addDepartmentFilter(params);
  addTagFilters(params);
  if (Array.isArray(dateRange.value)) {
    if (dateRange.value.length > 1) {
      const newStart = new Date(dateRange.value[0]).toISOString();
      params["timeStart[after]"] = newStart;
      const newEnd = new Date(dateRange.value[1]);
      newEnd.setHours(23);
      newEnd.setMinutes(59);
      newEnd.setSeconds(59);
      params["timeStart[before]"] = formatDate(newEnd);
      isActiveTotalHoursAll.value = true;
    }
  }

  TrackerService.getEntries(params)
    .then((response) => {
      const report = prepareTrackerReport(response.data);
      entries.value = report.entries;
      totalEntries.value = report.totalEntries;
      totalTimeAll.value = report.totalDuration;
    })
    .catch((error) => {
      console.log(error);
      notification.addNotification({
        message: "Error al cargar los registros",
        type: "error",
        duration: 6000,
      });
      uiStore.stopLoading();
    })
    .finally(() => {
      uiStore.stopLoading();
      recharge.value = false;
    });
};

const getNewProject = (newProject) => {
  if (projects.value.some((item) => item["@id"] === newProject["@id"])) {
    projects.value = projects.value.filter((item) => item["@id"] !== newProject["@id"]);
    if (projects.value.length >= 1) {
      project.value = projects.value[0];
    } else {
      project.value = null;
    }
  } else {
    projects.value.push(newProject);
    if (!project.value) {
      project.value = newProject;
    }
  }
  refDropdownProject.value.handleClose();
  dropdownProject.value = false;
  search();
};

const deleteProject = () => {
  project.value = null;
  projects.value = [];
  dropdownProject.value = false;
  refDropdownProject.value?.handleClose();
  search();
};

const openProjectDropdown = () => {
  dropdownProject.value = true;
};

const handleProjectDropdownVisibleChange = (visible) => {
  dropdownProject.value = visible;
};

//Paginacion
const changePagination = () => {
  search();
};

// Function Borrar filtros
const deleteFilters = () => {
  description.value = "";
  project.value = null;
  projects.value = [];
  client.value = null;
  employee.value = null;
  department.value = null;
  tags.value = [];
  dateRange.value = [];
  search();
};

const addTagFilters = (params) => {
  if (!tags.value.length) {
    return;
  }

  const ids = tags.value.map((tag) => tag.id);
  if (ids.length > 1) {
    params["tags.id[]"] = ids;
  } else {
    params["tags.id"] = ids[0];
  }
  isActiveTotalHoursAll.value = true;
};

const addDepartmentFilter = (params) => {
  if (!department.value) {
    return;
  }

  params["project.ownerDepartment.id"] = department.value.id;
  isActiveTotalHoursAll.value = true;
};

const setSelectedTagTitles = async () => {
  await nextTick();
  const selectElement = refReportTagsSelect.value?.$el;
  if (!selectElement) {
    return;
  }

  const selectedNames = tags.value.map((tag) => tag.name);
  selectElement.querySelectorAll(".el-tag").forEach((tagElement, index) => {
    const tagText = tagElement.textContent.trim();
    const title = tagText.startsWith("+")
      ? selectedNames.slice(index).join(", ")
      : selectedNames[index] || tagText;
    tagElement.setAttribute("title", title);
  });
};

const formatDate = (date) => {
  // Usar el offset de la fecha serializada, no el offset actual del navegador.
  const offset = date.getTimezoneOffset();
  const sign = offset > 0 ? "-" : "+";
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0");
  const minutes = String(Math.abs(offset) % 60).padStart(2, "0");

  // Formato de fecha
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  // Formato de hora
  const hoursOfDay = String(date.getHours()).padStart(2, "0");
  const minutesOfDay = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  // Devolver la cadena en formato ISO con el desplazamiento de la zona horaria
  return `${year}-${month}-${day}T${hoursOfDay}:${minutesOfDay}:${seconds}${sign}${hours}:${minutes}`;
};

const downloadReport = () => {
  if (!canDownloadReport.value) return;

  const params = {
    timeStart: dateRange.value[0],
    timeEnd: dateRange.value[1],
  };

  if (projects.value && projects.value.length > 1) {
    const ids = projects.value.map((project) => project.id);
    params["project.id[]"] = ids;
  }
  if (project.value && projects.value.length === 1) {
    params["project.id"] = project.value.id;
  }

  if (client.value) {
    params["client.id"] = client.value.id;
  }

  if (employee.value) {
    params["employee.id"] = employee.value.id;
  }

  addDepartmentFilter(params);
  addTagFilters(params);

  TrackerService.getReport(params).then((res) => {
    handleFileDownload(res, "xlsx");
    notification.addNotification({
      type: "success",
      message: "Informe descargado correctamente",
      duration: 6000,
    });
  });
};

// eslint-disable-next-line no-unused-vars
const handleFileDownload = (res, fileExtension) => {
  const fileName = getFileName(res, fileExtension);
  const blob = new Blob([res.data], {
    type: res.headers["content-type"],
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getFileName = (res, fileExtension) => {
  const contentDisposition = res.headers["content-disposition"];

  let fileName = `informe_trackernet.`;
  if (project.value) {
    fileName = fileName + `${project.value.name}`;
  }
  if (client.value) {
    fileName = fileName + `${client.value.name}`;
  }
  if (employee.value) {
    fileName = fileName + `${employee.value.fullName}`;
  }
  if (department.value) {
    fileName = fileName + `${department.value.name}`;
  }
  if (tags.value.length) {
    fileName = fileName + `${tags.value.map((tag) => tag.name).join("-")}`;
  }
  fileName = fileName + `.${dateRange.value[0]}.${dateRange.value[1]}.${fileExtension}`;
  if (contentDisposition) {
    const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = fileNameRegex.exec(contentDisposition);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, "");
    }
  }
  return fileName;
};
</script>

<template>
  <div v-if="user && (user.roles.includes('ROLE_ADMIN') || user.accessTrackerNetReports)"
    class="trackernet-report-body">
    <HeaderTitle title="Producción" />
    <div class="d-flex flex-row justify-content-start trackernet-header">
      <img src="@/assets/icons/tracker-net-blue.svg" alt="" />
      <div class="title-text ml-4">
        <!--          TrackerNet &bull; Informes-->
        TrackerNet &middot; Informes
      </div>
    </div>
    <el-row class="mt-2">
      <el-card class="card-search w-100">
        <div class="row report-filter-row">
          <!-- EMPLEADO -->
          <div class="col-2 el-col-xs-24">
            <el-select class="form-item" :value-key="'@id'" clearable filterable placeholder="Empleado"
              :filter-method="filterEmployeesByName"
              v-model="employee" @change="search">
              <el-option v-for="employee in filteredEmployeeOptions" :key="employee['@id']" :label="employee.fullName"
                :value="employee">
              </el-option>
            </el-select>
          </div>
          <!-- CLIENTE -->
          <div class="col-2 el-col-xs-24">
            <el-select class="form-item" :value-key="'@id'" clearable filterable remote
              :remote-method="getClients" :loading="optionsClientsLoading" placeholder="Cliente" v-model="client">
              <el-option v-for="cl in optionsClients" :key="cl['@id']" :label="cl.name" :value="cl">
              </el-option>
            </el-select>
          </div>
          <!-- PROYECTO -->
          <div class="col-2 el-col-xs-24 mb-3">
            <el-dropdown trigger="click" :hide-on-click="false" ref="refDropdownProject"
              class="addProjectDropdown report-project-select" :class="{ 'is-focused': dropdownProject }"
              placement="bottom" @visible-change="handleProjectDropdownVisibleChange">
              <p v-if="!project" @click="openProjectDropdown" class="report-project-placeholder">
                Seleccionar Proyecto
              </p>
              <!-- <el-tooltip class="box-item" effect="dark" :content="` tareas (click para desplegar)`" placement="top"
                v-if="project"> -->
              <div v-if="project" class="project-dropdown-trigger" @click="openProjectDropdown">
                <el-tooltip effect="dark" :content="projectsText" placement="top" v-if="project">
                  <tracker-project-info :project="project" class="el-dropdown-link" :editImport="true"
                    style="overflow: hidden" />
                </el-tooltip>
                <span v-if="projects.length > 1">+{{ projects.length - 1 }}</span>
                <el-button circle size="small" type="danger" style="margin-left: 8px; margin-top: 2px"
                  @click.stop="deleteProject">
                  <el-icon>
                    <Close />
                  </el-icon>
                </el-button>
              </div>
              <!-- </el-tooltip> -->
              <template #dropdown>
                <el-dropdown-menu class="dropProject">
                  <el-dropdown-item style="height: 300px">
                    <TrackerProjectSearch @get-project="getNewProject" :project="project" :client="client"
                      v-if="dropdownProject" :dropdown-open="dropdownProject" :selectedProjects="projects"
                      :optimized-selector="true" />
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
          <!-- DESCRIPCION -->
          <div class="col-2 el-col-xs-24 mb-3">
            <el-input v-model="description" clearable class="search-input" placeholder="Descripción" @change="search" />
          </div>
          <!-- FECHA -->
          <div class="col-2 el-col-xs-24 mb-3">
            <el-date-picker v-model="dateRange" type="daterange" unlink-panels start-placeholder="Desde"
              end-placeholder="Hasta" format="DD/MM/YYYY" value-format="YYYY-MM-DD" clearable @change="search"
              :shortcuts="dateShortcuts" class="w-100 rounded-pill border border-black dateRange" />
          </div>
          <!-- BOTON BUSCAR -->
          <div class="col-2 el-col-xs-24 mb-3 report-search-column">
            <button class="btn btn_red btn-search report-search-button" @click="search()">
              BUSCAR
            </button>
          </div>
          <!-- ETIQUETAS -->
          <div class="col-2 el-col-xs-24 mb-3 report-tags-column">
            <el-select ref="refReportTagsSelect" :value-key="'@id'" clearable filterable multiple collapse-tags
              collapse-tags-tooltip placeholder="Etiqueta" v-model="tags" popper-class="dropdownEntryTags"
              class="tracker-select responsive-select report-tags-select" @change="search">
              <el-option v-for="tag in optionTags" :key="tag['@id']" :label="tag.name" :value="tag">
              </el-option>
            </el-select>
          </div>
          <!-- Propietario -->
          <div class="col-2 el-col-xs-24 mb-3 report-department-column">
            <el-select class="form-item report-department-select" :value-key="'@id'" clearable filterable
              placeholder="Propietario" v-model="department" @change="search">
              <el-option v-for="department in optionsDepartments" :key="department['@id']" :label="department.name"
                :value="department">
              </el-option>
            </el-select>
          </div>
          <div class="col-12 el-col-xs-24 mb-3 report-group-days-mobile-column">
            <el-checkbox v-if="canGroupEntriesByDay" v-model="groupEntriesByDay"
              class="group-days-checkbox group-days-checkbox-mobile">
              Agrupar por días
            </el-checkbox>
          </div>
          <div class="col-8 el-col-xs-24 mb-3 report-actions-column">
            <div class="report-actions">
              <el-checkbox v-if="canGroupEntriesByDay" v-model="groupEntriesByDay"
                class="group-days-checkbox group-days-checkbox-desktop">
                Agrupar por días
              </el-checkbox>
              <button class="btn btn_yellow btn-search report-download-button" :disabled="!canDownloadReport"
                @click="downloadReport()">
                DESCARGAR INFORME
              </button>
            </div>
          </div>
        </div>
      </el-card>
    </el-row>
    <!-- BORRAR FILTROS -->
    <div class="d-flex">
      <span class="ms-auto deleteFilter" @click="deleteFilters">Borrar filtros</span>
    </div>
    <!-- INFORMES -->
    <div v-if="!recharge">
      <div>
        <TrackerFullReport :entries="entries" v-if="entries.length > 0 && !recharge" @reload-entries="search"
          :totalHoursAll="totalTimeAll" :isActiveHoursAll="isActiveTotalHoursAll" :groupByDay="groupEntriesByDay" />
      </div>
      <!-- PAGINACION -->
      <div class="mt-4">
        <SharedElementPagination v-model:current-page="pageEntries" v-model:page-size="itemsPerPageEntries"
          size="default" :page-size-options="[50, 100, 200]" layout="prev, pager, next, sizes" :total="totalEntries"
          class="tracker-pagination" show-always @change="changePagination" />
      </div>
    </div>
  </div>
  <div v-else>No tienes permiso para ver esta sección</div>
</template>

<style scoped lang="scss">
.card-search {
  height: auto;
  border-radius: 15px;
  // max-height: 200px;
  background: white;
  background-position-x: 100%;
  overflow: visible !important;
}

.btn-search {
  &.btn_red {
    color: white;
  }
}

.date-picker-tracker {
  .el-date-editor {
    border-radius: 25px !important;
    width: 100% !important;
  }
}

.addProjectDropdown {
  align-items: center;
  border: 1px solid #1f1f1f;
  border-radius: 24px;
  box-sizing: border-box;
  display: flex;
  height: 44px;
  margin-bottom: 0;
  margin-top: 0;
  padding: 0 16px;
  width: 100%;
}

.addProjectDropdown:hover,
.addProjectDropdown.is-focused {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.14);
}

.report-project-placeholder {
  color: #a8abb2;
  cursor: pointer;
  font-size: 14px;
  line-height: 42px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-dropdown-trigger {
  align-items: center;
  cursor: pointer;
  display: flex;
  margin-left: 0;
  margin-top: 0;
  min-width: 0;
  width: 100%;
}

.report-filter-row {
  row-gap: 12px;
}

.report-search-column,
.report-tags-column,
.report-department-column {
  align-items: center;
  display: flex;
  min-width: 0;
}

.report-search-column {
  justify-content: flex-end;
}

.report-tags-column,
.report-department-column {
  :deep(.el-select) {
    height: 44px;
    margin-bottom: 0;
    width: 100%;
  }

  :deep(.el-select__wrapper) {
    border-radius: 24px !important;
    box-sizing: border-box;
    height: 44px !important;
    min-height: 44px !important;
    padding-left: 16px;
    padding-right: 16px;
    width: 100%;
  }

  :deep(.el-select__placeholder) {
    color: #a8abb2;
  }
}

.report-tags-select {

  :deep(.el-tag),
  :deep(.el-tag__content),
  :deep(.el-select__tags-text) {
    max-width: 95px !important;
  }

  :deep(.el-tag__content),
  :deep(.el-select__tags-text) {
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.group-days-checkbox {
  color: #474ce6;
  font-weight: 500;
  margin-bottom: 0;
  white-space: nowrap;
}

.group-days-checkbox-mobile,
.report-group-days-mobile-column {
  display: none;
}

.report-actions {
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: flex-end;
  min-height: 100%;
}

.report-actions-column {
  display: flex;
  justify-content: flex-end;
}

.report-search-button,
.report-download-button {
  margin-bottom: 0 !important;
  margin-right: 0 !important;
  min-width: 220px;
  width: 220px;
}

.report-download-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.report-download-button {
  color: #474ce6 !important;
}

.report-search-button,
.report-download-button {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  min-height: 44px;
}

@media (min-width: 768px) {
  .responsive-select {
    width: 100%;

    :deep(.el-select__wrapper) {
      align-items: center;
      min-height: 32px;
    }

    :deep(.el-select__selection) {
      align-items: center;
      display: flex;
      flex-wrap: nowrap;
      min-width: 0;
    }

    :deep(.el-select__selected-item) {
      align-items: center;
      flex-shrink: 0;
    }

    :deep(.el-select__input-wrapper) {
      flex: 1 1 auto;
      min-width: 70px;
    }
  }
}

@media (max-width: 1630px) {
  .dateRange {
    .el-range-editor.el-input__wrapper {
      padding: 0;
    }
  }
}

@media (max-width: 767px) {
  .trackernet-report-body {
    #header-title {
      height: 75px !important;
    }

    .trackernet-header {
      .title-text {
        font-size: 24px !important;
      }

      img {
        width: 30px;
      }
    }
  }

  .group-days-checkbox {
    white-space: normal;
  }

  .group-days-checkbox-mobile {
    display: inline-flex;
  }

  .group-days-checkbox-desktop {
    display: none;
  }

  .report-actions {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  .report-actions-column {
    justify-content: stretch;
  }

  .report-search-column,
  .report-tags-column,
  .report-department-column,
  .report-group-days-mobile-column,
  .report-actions-column {
    flex: 0 0 100%;
    max-width: 100%;
    width: 100%;
  }

  .report-search-column {
    justify-content: stretch;
    order: 9;
  }

  .report-tags-column {
    order: 6;
  }

  .report-department-column {
    order: 7;
  }

  .report-group-days-mobile-column {
    display: flex;
    order: 8;
  }

  .report-actions-column {
    order: 10;
  }

  .report-tags-select,
  .report-department-select {
    width: 100%;
  }

  .report-search-button,
  .report-download-button {
    box-sizing: border-box;
    min-width: 100%;
    width: 100%;
  }
}
</style>
